import { TezosToolkit } from '@taquito/taquito';
import { MichelsonMap } from '@taquito/taquito';
import NETWORKS from './networkconfig.js';

// Helper to get the market contract
export async function getMarketContract(network, tezos) {
    const marketAddr = NETWORKS[network].marketbase;
    if (!marketAddr) throw new Error("No market contract for this network");
    return await tezos.wallet.at(marketAddr);
}

// Check if operator is set for given user and token
export async function isOperatorForToken(walletManager, ownerAddress, operatorAddress, tokenId, contractAddress) {
  const tezos = walletManager.tezos;
  const contract = await tezos.contract.at(contractAddress);
  const storage = await contract.storage();

  let operatorsBigMap;
  try {
    operatorsBigMap = storage.operators;
  } catch (err) {
    console.error('Operators big map not found:', err);
    return false;
  }

  try {
    const operatorKey = {
      owner: ownerAddress,
      operator: operatorAddress,
      token_id: tokenId,
    };
    const value = await operatorsBigMap.get(operatorKey);
    return !!value;
  } catch {
    return false;
  }
}

// Add or remove the escrow contract as operator for a token
export async function setOperatorForToken(walletManager, operatorAddress, tokenId, contractAddress, addOperator = true) {
  const contract = await walletManager.tezos.wallet.at(contractAddress);
  const update = addOperator
    ? { add_operator: { owner: walletManager.address, operator: operatorAddress, token_id: tokenId } }
    : { remove_operator: { owner: walletManager.address, operator: operatorAddress, token_id: tokenId } };

  const op = await contract.methods.update_operators([update]).send();
  await op.confirmation();
  return op;
}

// Initialize escrow via init_escrow entrypoint
export async function initEscrow(walletManager, contractAddress, tokenId, amount) {
  const contract = await walletManager.tezos.wallet.at(contractAddress);
  const op = await contract.methods.init_escrow(tokenId, amount).send();
  await op.confirmation();
  return op;
}

// Taker deposits into escrow via taker_deposit entrypoint
export async function takerDeposit(walletManager, contractAddress, tokenId, amount) {
  const contract = await walletManager.tezos.wallet.at(contractAddress);
  const op = await contract.methods.taker_deposit(tokenId, amount).send();
  await op.confirmation();
  return op;
}

// Cancel escrow by ID via cancel_escrow entrypoint
export async function cancelEscrow(walletManager, escrowId, contractAddress) {
  const contract = await walletManager.tezos.wallet.at(contractAddress);
  const op = await contract.methods.cancel_escrow(escrowId).send();
  await op.confirmation();
  return op;
}

// Maker approves escrow by ID via maker_approve entrypoint
export async function makerApprove(walletManager, escrowId, contractAddress) {
  const contract = await walletManager.tezos.wallet.at(contractAddress);
  const op = await contract.methods.maker_approve(escrowId).send();
  await op.confirmation();
  return op;
}

export async function fetchOpenEscrows(walletManager, walletAddress, contractAddress) {
  const tezos = walletManager.tezos;
  const contract = await tezos.contract.at(contractAddress);

  // Call the on-chain view to get user offers (escrow_id and role)
  const userOffers = await contract.contractViews.get_user_offers(walletAddress).executeView({viewCaller: contractAddress});

  const escrows = [];

  // For each escrow record, fetch the full escrow details with get_escrow_by_id view
  for (const offer of userOffers) {
    const escrowId = offer.escrow_id.toNumber ? offer.escrow_id.toNumber() : offer.escrow_id;
    const escrowInfo = await contract.contractViews.get_escrow_by_id(escrowId).executeView({viewCaller: contractAddress});

    console.log("escroInfo", escrowInfo);

    // Filter escrows based on roles and states
    const escrowStatus = escrowInfo.state;

    // IMPORTANT: Skip CANCELLED and COMPLETED escrows
    if (escrowStatus === 'CANCELLED' || escrowStatus === 'SWAPPED') {
      continue;
    }

    // Only include relevant escrows similar to your original logic
    const isMakerRelevant = offer.role === 'maker';
    const isTakerRelevant = offer.role === 'taker';

    if (isMakerRelevant || isTakerRelevant) {
      escrows.push({
        escrowId,
        maker: escrowInfo.maker,
        taker: escrowInfo.taker,
        makerApproved: escrowInfo.maker_approved,
        makerDeposited: escrowInfo.maker_deposited,
        takerApproved: escrowInfo.taker_approved,
        takerDeposited: escrowInfo.taker_deposited,
        makerToken: escrowInfo.maker_token,   // full token info: amount, token_address, token_id
        takerToken: escrowInfo.taker_token,   // can be null
        status: escrowInfo.state,
        role: offer.role                       // from user's role in this escrow
      });
    }
  }

  return escrows;
}